import React from 'react';
import { useLoading } from '@/contexts/LoadingContext';

interface Options<T> {
  nursingHomeId: string;
  userId: number;
  serviceType: string;
  permittedUnitId: number | null;
  fetchData: (permittedUnitId: number | null) => Promise<T>;
}

// 通知・アドバイスの取得範囲は閲覧権限で固定する。
// 全ユニット権限は施設全体、ユニット権限は自身のユニットから50件取得する
// （Professional通知は尿・便それぞれ50件）。
// ユニットを含むチェック変更は取得済みデータだけで絞り込み、50件未満になっても補充取得しない。
export const usePermissionScopedData = <T,>({
  nursingHomeId, userId, serviceType, permittedUnitId, fetchData,
}: Options<T>) => {
  const scope = JSON.stringify([nursingHomeId, userId, serviceType, permittedUnitId]);
  const enabled = !!userId && !!serviceType;
  const loading = useLoading();
  const latest = React.useRef({ scope, enabled, fetchData, loading });
  React.useEffect(() => { latest.current = { scope, enabled, fetchData, loading }; }, [scope, enabled, fetchData, loading]);
  const requestId = React.useRef(0);
  const mounted = React.useRef(false);
  const [source, setSource] = React.useState<{ scope: string; status: 'loading' | 'success' | 'error'; data?: T }>();

  React.useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const refresh = React.useCallback(async () => {
    if (!enabled || !mounted.current || !latest.current.enabled || latest.current.scope !== scope) return;
    const currentRequest = ++requestId.current;
    setSource((previous) => ({ scope, status: 'loading', data: previous?.scope === scope ? previous.data : undefined }));
    latest.current.loading.showLoading();
    try {
      const data = await latest.current.fetchData(permittedUnitId);
      if (currentRequest !== requestId.current || !mounted.current) return;
      setSource({ scope, status: 'success', data });
    } catch {
      if (currentRequest !== requestId.current || !mounted.current) return;
      setSource((previous) => ({ scope, status: 'error', data: previous?.scope === scope ? previous.data : undefined }));
    } finally {
      if (currentRequest === requestId.current && mounted.current) latest.current.loading.hideLoading();
    }
  }, [enabled, scope, permittedUnitId]);

  React.useEffect(() => {
    void refresh();
    return () => {
      requestId.current += 1;
      latest.current.loading.hideLoading();
    };
  }, [refresh]);

  const current = enabled && source?.scope === scope ? source : undefined;
  return {
    data: current?.data,
    isFetching: !current || current.status === 'loading',
    hasFetchError: current?.status === 'error',
    refresh,
  };
};
